import {Trans} from '@lingui/macro';
import React, {FC, useCallback} from 'react';

import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {BoxedIcon} from '../../../components/common/boxedIcon';
import {SettingsSearchInboxModel} from '../../../models/api/settingsSearchModel';
import {TeamModel} from '../../../models/api/teamModel';
import {maybeRenderAccessory} from './settingsSearchAccessoryHelpers';
import {
  SettingsSearchDropdownContent,
  SettingsSearchDropdownItem,
  SettingsSearchDropdownItemLeftIcon,
  SettingsSearchDropdownSubtitle,
  SettingsSearchDropdownTitle,
} from './settingsSearchCommon';
import {SettingsSearchDropdownItemBaseProps} from './settingsSearchCommonTypes';

/*
 * Props.
 */

interface SettingsSearchDropdownInboxItemProps extends SettingsSearchDropdownItemBaseProps {
  inboxItem: SettingsSearchInboxModel['items'][0];
  team?: TeamModel;
}

/*
 * Component.
 */

export const SettingsSearchDropdownInboxItem: FC<SettingsSearchDropdownInboxItemProps> = (props) => {
  const theme = useUserTheme();
  const {inboxItem} = props;
  const {inbox, channelNames, ruleCount} = inboxItem;

  const backgroundColor = inbox.color.toLowerCase() === '#ffffff' ? theme.greys.shade50 : inbox.color;

  const renderDescription = useCallback(() => {
    const numberOfChannels = channelNames.length;
    const channelsBaseDescription = channelNames.slice(0, 3).join(', ');
    const channelsDescriptionSuffix =
      channelNames.slice(0, 3).length < numberOfChannels ? (
        <Trans id="searchdropdown-channelssuffix">+ {channelNames.length - 3} channels</Trans>
      ) : (
        ''
      );
    const channelsDescription = `${channelsBaseDescription} ${channelsDescriptionSuffix}`.trim();

    if (numberOfChannels < 1 && ruleCount < 1) {
      return <Trans id="searchdropdown-nochannelsorrules">No channels or move rules.</Trans>;
    }

    if (ruleCount < 1) {
      return channelsDescription;
    }

    if (numberOfChannels < 1) {
      return <Trans id="searchdropdown-rulescount">{ruleCount} rules</Trans>;
    }

    return (
      <Trans id="searchdropdown-channeldescrulecount">
        {channelsDescription}, {ruleCount} rules
      </Trans>
    );
  }, [channelNames, ruleCount]);

  return (
    <SettingsSearchDropdownItem checkmarkLabel={inbox.name} onSelect={props.onSelect}>
      <SettingsSearchDropdownItemLeftIcon>
        <BoxedIcon
          name="inboxFilledSmall"
          backgroundColor={backgroundColor}
          color={theme.greys.white}
          size={VisualSizesEnum.SMALL}
        />
      </SettingsSearchDropdownItemLeftIcon>
      <SettingsSearchDropdownContent>
        <SettingsSearchDropdownTitle>
          {inbox.name}
          {maybeRenderAccessory(inbox, props.team)}
        </SettingsSearchDropdownTitle>
        <SettingsSearchDropdownSubtitle>{renderDescription()}</SettingsSearchDropdownSubtitle>
      </SettingsSearchDropdownContent>
    </SettingsSearchDropdownItem>
  );
};
